import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { editEmployee, fetchEmployees } from "../../../services/Employee/EmployeeApi";
import { fetchActiveBranches } from "../../../services/apiServiceDashboard";
import { getAllUsers } from "../../../services/Users";

// Props:
// - open: boolean
// - employeeId: id of employee to edit
// - onClose(): close dialog
// - onSaved(updated): called with updated employee object after success
const EditeEmployeeData = ({ open, employeeId, onClose, onSaved }) => {
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    address: "",
    phone: "",
    role: "",
    monthly_salary: "",
    weekly_salary: "",
    daily_salary: "",
    salary_type: "monthly",
    user_id: "",
    root_id: "",
    nationa_id: null,
    active: "",
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const [branches, setBranches] = useState([]);
  const [branchesMap, setBranchesMap] = useState({});
  const [users, setUsers] = useState([]);
  const [usersMap, setUsersMap] = useState({});

  // load lists
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const resB = await fetchActiveBranches();
        const listB = Array.isArray(resB) ? resB : (resB?.data && Array.isArray(resB.data) ? resB.data : []);
        if (!mounted) return;
        setBranches(listB);
        const mapB = {};
        listB.forEach((b) => { if (b && b.id !== undefined) mapB[b.id] = b.name ?? b.title ?? String(b.id); });
        setBranchesMap(mapB);
      } catch (err) {
        console.error(err);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    const loadUsers = async () => {
      try {
        const res = await getAllUsers();
        const list = Array.isArray(res) ? res : (res?.data && Array.isArray(res.data) ? res.data : []);
        if (!mounted) return;
        setUsers(list);
        const map = {};
        list.forEach((u) => { if (u) map[u.id] = u.name ?? u.username ?? u.full_name ?? String(u.id); });
        setUsersMap(map);
      } catch (err) {
        console.error(err);
      }
    };
    loadUsers();
    return () => { mounted = false; };
  }, []);

  // load specific employee data when employeeId or open changes
  useEffect(() => {
    if (!open || !employeeId) {
      // clear form when closed
      setForm((prev) => ({ ...prev, name: "", email: "", address: "", phone: "", role: "", monthly_salary: "", weekly_salary: "", daily_salary: "", salary_type: "monthly", user_id: "", root_id: "", nationa_id: null, active: "" }));
      return;
    }
    let mounted = true;
    const loadEmployee = async () => {
      setLoadingEmployee(true);
      try {
        const all = await fetchEmployees();
        const list = Array.isArray(all) ? all.flat(Infinity) : [];
        const emp = list.find((x) => String(x.id) === String(employeeId));
        if (!mounted) return;
        if (emp) {
          setForm({
            name: emp.name ?? "",
            email: emp.email ?? "",
            address: emp.address ?? "",
            phone: emp.phone ?? "",
            role: emp.role ?? "",
            monthly_salary: emp.monthly_salary ?? "",
            weekly_salary: emp.weekly_salary ?? "",
            daily_salary: emp.daily_salary ?? "",
            salary_type: emp.salary_type ?? "monthly",
            user_id: emp.user_id ?? (emp.user && (emp.user.id ?? emp.user.user_id)) ?? "",
            root_id: emp.root_id ?? (emp.root && emp.root.id) ?? "",
            nationa_id: null,
            active: emp.active ?? "",
          });
        } else {
          // if not found, keep id only
          setForm((prev) => ({ ...prev }));
        }
      } catch (err) {
        console.error(err);
        setSnackbar({ open: true, message: "فشل في جلب بيانات الموظف", severity: "error" });
      } finally {
        if (mounted) setLoadingEmployee(false);
      }
    };
    loadEmployee();
    return () => { mounted = false; };
  }, [employeeId, open]);

  const handleChange = (e) => {
    const { name, value, files, type } = e.target;
    if (type === "file") {
      setForm((prev) => ({ ...prev, [name]: files && files[0] ? files[0] : null }));
    } else {
      if ((name === "user_id" || name === "root_id") && value !== "") {
        const num = Number(value);
        setForm((prev) => ({ ...prev, [name]: Number.isNaN(num) ? value : num }));
      } else {
        setForm((prev) => ({ ...prev, [name]: value }));
      }
    }
  };

  const handleSalaryChange = (e) => {
    const val = e.target.value;
    setForm((prev) => {
      const key = `${prev.salary_type}_salary`;
      return { ...prev, [key]: val };
    });
  };

  const handleSubmit = async () => {
    if (!employeeId) {
      setSnackbar({ open: true, message: "لا يوجد معرف موظف", severity: "error" });
      return;
    }
    setSubmitLoading(true);
    try {
      const payload = { ...form };
      const res = await editEmployee(employeeId, payload);
      const updated = res?.data ? (Array.isArray(res.data) ? res.data.flat(Infinity)[0] : res.data) : null;
      setSnackbar({ open: true, message: "تم تحديث الموظف بنجاح", severity: "success" });
      if (onSaved) onSaved(updated ?? { id: employeeId, ...payload });
      if (onClose) onClose();
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: err.message || "فشل في تعديل الموظف", severity: "error" });
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <Dialog open={!!open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>تعديل بيانات الموظف</Box>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {loadingEmployee ? (
            <Box sx={{ textAlign: "center", py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <TextField fullWidth label="اسم الموظف" name="name" margin="dense" value={form.name} onChange={handleChange} />
              <TextField fullWidth label="البريد الإلكتروني" name="email" margin="dense" value={form.email} onChange={handleChange} />
              <TextField fullWidth label="العنوان" name="address" margin="dense" value={form.address} onChange={handleChange} />
              <TextField fullWidth label="الهاتف" name="phone" margin="dense" value={form.phone} onChange={handleChange} />
              <TextField fullWidth label="الدور" name="role" margin="dense" value={form.role} onChange={handleChange} />

              <TextField
                select
                fullWidth
                label="المستخدم"
                name="user_id"
                margin="dense"
                value={form.user_id ?? ""}
                onChange={handleChange}
              >
                <MenuItem value="">-- اختر مستخدم --</MenuItem>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {usersMap[u.id] ?? u.name ?? u.username ?? String(u.id)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="الفرع"
                name="root_id"
                margin="dense"
                value={form.root_id ?? ""}
                onChange={handleChange}
              >
                <MenuItem value="">-- اختر فرع --</MenuItem>
                {branches.map((b) => (
                  <MenuItem key={b.id} value={b.id}>
                    {branchesMap[b.id] ?? b.name ?? b.title ?? String(b.id)}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                select
                fullWidth
                label="نوع الراتب"
                name="salary_type"
                margin="dense"
                value={form.salary_type}
                onChange={(e) => setForm((p) => ({ ...p, salary_type: e.target.value }))}
              >
                <MenuItem value="monthly">monthly</MenuItem>
                <MenuItem value="daily">daily</MenuItem>
                <MenuItem value="weekly">weekly</MenuItem>
              </TextField>

              <TextField fullWidth label="الراتب" margin="dense" value={form[`${form.salary_type}_salary`] || ""} onChange={handleSalaryChange} />

              <Box sx={{ mt: 1 }}>
                <input type="file" name="nationa_id" accept="image/*" onChange={handleChange} />
              </Box>

              <TextField
                select
                fullWidth
                label="الحالة"
                name="active"
                margin="dense"
                value={form.active !== undefined ? String(form.active) : ""}
                onChange={(e) => setForm((p) => ({ ...p, active: e.target.value === "" ? "" : Number(e.target.value) }))}
              >
                <MenuItem value="">-- اختر الحالة --</MenuItem>
                <MenuItem value={1}>نشط</MenuItem>
                <MenuItem value={0}>غير نشط</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={submitLoading || loadingEmployee}>
            {submitLoading ? <CircularProgress size={20} /> : "حفظ التعديل"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default EditeEmployeeData;